interface LegacyRedirectParams {
  orgDomain: string;
  propertyId: number;
  entrataRedirectLink?: string;
}

/** Path template for legacy navigation (dev/test: process.env; deployed: window.env). */
export function getLegacyApplicationRedirectPathTemplate(): string | undefined {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    return process.env.LEGACY_APPLICATION_REDIRECT_PATH_TEMPLATE;
  }
  return (
    (window as Window & { env?: { LEGACY_APPLICATION_REDIRECT_PATH_TEMPLATE?: string } }).env
      ?.LEGACY_APPLICATION_REDIRECT_PATH_TEMPLATE ||
    process.env.LEGACY_APPLICATION_REDIRECT_PATH_TEMPLATE
  );
}

/**
 * Redirects the user to the legacy application via GET without forwarding credentials.
 * If running inside an iframe, navigates the top-level browser directly.
 * If running in the top-level window, navigates directly.
 *
 * @param params - The redirect parameters including org domain, property ID, and optional entrataRedirectLink
 * @throws {Error} If the LEGACY_APPLICATION_REDIRECT_PATH_TEMPLATE environment variable is not configured or if entrataRedirectLink is not provided
 */
export const redirectToLegacyApplication = (params: LegacyRedirectParams): void => {
  const { propertyId, entrataRedirectLink } = params;

  const pathTemplate = getLegacyApplicationRedirectPathTemplate();

  if (!pathTemplate) {
    // eslint-disable-next-line no-console
    console.error('LEGACY_APPLICATION_REDIRECT_PATH_TEMPLATE not configured');
    throw new Error('Legacy application redirect not configured');
  }

  // Get the base URL from query parameter
  if (!entrataRedirectLink) {
    // eslint-disable-next-line no-console
    console.error('entrataRedirectLink query parameter not provided');
    throw new Error('Legacy application base URL not configured');
  }

  // Ensure the URL uses HTTPS
  let legacyBaseUrl = entrataRedirectLink;
  if (!window.location.hostname.includes('localhost') && legacyBaseUrl.startsWith('http://')) {
    legacyBaseUrl = legacyBaseUrl.replace('http://', 'https://');
    // eslint-disable-next-line no-console
    console.warn('Upgraded entrataRedirectLink from HTTP to HTTPS');
  }

  // Construct full URL by replacing {propertyId} in template
  // Keep existing deployment templates compatible while opening the public
  // login page rather than attempting to process a login without credentials.
  const path = pathTemplate
    .replace('/action/process_login/', '/action/view_login/')
    .replace('{propertyId}', propertyId.toString());

  // Append the legacy_fallback flag to break the infinite redirect loop in the monolith
  const separator = path.includes('?') ? '&' : '?';
  const fullUrl = `${legacyBaseUrl}${path}${separator}legacy_fallback=1`;

  // Show loading overlay
  const loadingOverlay = document.createElement('div');
  loadingOverlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.95);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  `;

  const spinner = document.createElement('div');
  spinner.style.cssText = `
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
  `;

  const loadingText = document.createElement('div');
  loadingText.textContent = 'Loading application...';
  loadingText.style.cssText = `
    margin-top: 20px;
    font-size: 18px;
    color: #333;
    font-weight: 500;
  `;

  // Add keyframe animation for spinner
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  loadingOverlay.appendChild(spinner);
  loadingOverlay.appendChild(loadingText);
  document.head.appendChild(style);
  document.body.appendChild(loadingOverlay);

  // Keep navigation inside the viewer; no SDK version or message is required.
  if (window.parent !== window) {
    window.top!.location.replace(fullUrl);
  } else {
    window.location.assign(fullUrl);
  }
};
